import type { SupportedLocale } from "./i18n";

export interface FormatDateTimeOptions {
  /** Interface locale (`fr`/`en`); defaults to `fr`. */
  locale?: SupportedLocale | string;
  /**
   * REQUIRED IANA venue timezone (DOMAIN.md §B.4 `TimeZoneId` / §C.1 `Venue.timeZone`, default
   * `Europe/Paris`). Rendering is venue-local (localization-i18n R5), never device-local.
   */
  timeZone: string;
}

/**
 * Venue-local date+time rendering (localization-i18n R5) on native `Intl` — no date library
 * (ADR-0013). Assembled from `Intl.DateTimeFormat` parts with an EXPLICIT `timeZone`, so the output
 * is identical regardless of the DEVICE timezone (proven by the `TZ=America/New_York` vitest run).
 *
 * FR: `dd/MM/yyyy` + 24-hour `HH:mm`. EN: `MM/dd/yyyy` + 12-hour `hh:mm AM/PM` — same instant, same
 * venue wall-clock time.
 */
export function formatDateTime(
  isoInstant: string | number | Date,
  { locale = "fr", timeZone }: FormatDateTimeOptions,
): string {
  const date = new Date(isoInstant);
  const isEnglish = String(locale).toLowerCase().startsWith("en");
  const parts = new Intl.DateTimeFormat(isEnglish ? "en-US" : "fr-FR", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: isEnglish,
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? "";
  const day = get("day");
  const month = get("month");
  const year = get("year");
  const hour = get("hour");
  const minute = get("minute");
  if (isEnglish) {
    return `${month}/${day}/${year}, ${hour}:${minute} ${get("dayPeriod")}`;
  }
  return `${day}/${month}/${year} ${hour}:${minute}`;
}
